import { LitElement, html, css } from 'lit';
import '../shared/markdown-element.js';

export class HelpSidebar extends LitElement {
  static properties = {
    isOpen: { type: Boolean },
    currentSection: { type: String }
  };

  constructor() {
    super();
    this.isOpen = false;
    this.currentSection = 'quick-start';
  }

  static styles = css`
    :host {
      display: block;
    }

    .overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 999;
      animation: fadeIn 0.2s ease;
    }

    .sidebar {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      width: 90%;
      max-width: 800px;
      background: white;
      z-index: 1000;
      display: flex;
      flex-direction: column;
      box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
      animation: slideInRight 0.3s ease;
    }

    @media (min-width: 768px) {
      .sidebar {
        width: 600px;
      }
    }

    @media (min-width: 1024px) {
      .sidebar {
        width: 800px;
      }
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1.5rem;
      border-bottom: 1px solid #e5e7eb;
      background: #f9fafb;
      flex-shrink: 0;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .header-icon {
      width: 2rem;
      height: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #3b82f6;
      color: white;
      border-radius: 8px;
      font-size: 1.25rem;
    }

    .header-title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #111827;
    }

    .close-button {
      background: transparent;
      border: none;
      color: #6b7280;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
    }

    .close-button:hover {
      background: #f3f4f6;
      color: #111827;
    }

    .content-wrapper {
      display: flex;
      flex: 1;
      overflow: hidden;
    }

    .nav {
      width: 200px;
      border-right: 1px solid #e5e7eb;
      background: #f9fafb;
      overflow-y: auto;
      flex-shrink: 0;
      padding: 1rem 0;
    }

    @media (max-width: 640px) {
      .nav {
        display: none;
      }
    }

    .nav-section {
      margin-bottom: 1.5rem;
    }

    .nav-section-title {
      padding: 0.5rem 1rem;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #6b7280;
    }

    .nav-item {
      display: block;
      padding: 0.5rem 1rem;
      color: #374151;
      text-decoration: none;
      font-size: 0.875rem;
      cursor: pointer;
      transition: all 0.15s ease;
      border-left: 3px solid transparent;
    }

    .nav-item:hover {
      background: rgba(59, 130, 246, 0.05);
      color: #3b82f6;
    }

    .nav-item.active {
      background: rgba(59, 130, 246, 0.1);
      color: #3b82f6;
      border-left-color: #3b82f6;
      font-weight: 500;
    }

    .content {
      flex: 1;
      overflow-y: auto;
      padding: 2rem;
    }

    @media (max-width: 640px) {
      .content {
        padding: 1rem;
      }
    }

    /* Scrollbar styling */
    .nav::-webkit-scrollbar,
    .content::-webkit-scrollbar {
      width: 6px;
    }

    .nav::-webkit-scrollbar-track,
    .content::-webkit-scrollbar-track {
      background: transparent;
    }

    .nav::-webkit-scrollbar-thumb,
    .content::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 3px;
    }

    .nav::-webkit-scrollbar-thumb:hover,
    .content::-webkit-scrollbar-thumb:hover {
      background: #9ca3af;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideInRight {
      from {
        opacity: 0;
        transform: translateX(100%);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    /* Custom markdown styling for help content */
    markdown-element {
      --color-primary: #3b82f6;
      --color-primary-hover: #2563eb;
    }

    /* Section headers in content */
    .section-header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
      padding-bottom: 1rem;
      border-bottom: 2px solid #e5e7eb;
    }

    .section-icon {
      width: 2.5rem;
      height: 2.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      color: white;
      border-radius: 10px;
      font-size: 1.5rem;
    }

    .section-title {
      margin: 0;
      font-size: 1.875rem;
      font-weight: 600;
      color: #111827;
    }

    /* Search box */
    .search-box {
      padding: 1rem;
      border-bottom: 1px solid #e5e7eb;
    }

    .search-input {
      width: 100%;
      padding: 0.625rem 0.75rem;
      padding-left: 2.5rem;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      font-size: 0.875rem;
      background: white;
      position: relative;
    }

    .search-input:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .search-wrapper {
      position: relative;
    }

    .search-icon {
      position: absolute;
      left: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
      width: 1rem;
      height: 1rem;
    }
  `;

  _close() {
    this.dispatchEvent(new CustomEvent('close'));
  }

  _selectSection(section) {
    this.currentSection = section;
    this.requestUpdate();
    
    // Scroll content area to top after update
    this.updateComplete.then(() => {
      const contentArea = this.shadowRoot.querySelector('.content');
      if (contentArea) {
        contentArea.scrollTop = 0;
      }
    });
  }

  _getContent() {
    const sections = {
      'quick-start': {
        icon: '⭐',
        title: 'Quick Start',
        content: `
# Quick Start: Your First 5 Minutes

## What is Kablewy?

Kablewy is a **context infrastructure platform**—build once, use with any AI model:

**📚 Context First:** Your documents, data, and workflows are permanent infrastructure. Upload once, search forever.

**🔄 Model Second:** Switch between GPT-5, Gemini, Claude, Grok freely as better models emerge. Your context stays.

**🛠️ Unified Tools:** Comprehensive capabilities work consistently across all models—no relearning, no rebuilding.

**🏗️ Build Value Over Time:** As you add documents, create workflows, and connect apps, your AI workspace gets smarter—regardless of which models you use.

---

## 1. Pick Your AI Model
Click the model selector (top of chat):

**🛠️ Speed vs 🎯 Capability:**
- **Fast** (Gemini Flash, Kimi K2) → Instant, great for quick tasks
- **Thorough** (GPT-5) → Best results, slower, worth it for complex work
- **Huge Context** (Grok-4 Fast, Gemini) → Handle massive documents

> 💡 **About the numbers:** "128K", "1M", etc. = context window (AI's working memory)
> • 128K ≈ 300 pages • 400K ≈ 1,000 pages • 1M ≈ 2,500 pages • 2M ≈ 5,000 pages
> Bigger = more documents, longer conversations without forgetting.

**Always Available (No Setup Required):**
- **Gemini 2.5 Flash** → Lightning fast, multimodal, huge memory (1M = ~2,500 pages) ⭐
- **GPT-5** → Best task completion, slower but thorough (400K = ~1,000 pages) ⭐
- **Kimi K2** → Ultra-fast responses (128K = ~300 pages)
- **Grok-4 Fast** → Ultra-fast, massive memory (2M = ~5,000 pages)
- **gpt-oss-120b** → Instant responses (131K)

**Premium (Add API Key to Unlock):**
- **Gemini 2.5 Pro** 🔑 → Professional grade (1M) - Google key
- **Claude Sonnet 4.5** 🔑 → Writing excellence (200K) - Anthropic key
- **Grok-4** 🔑 → Advanced reasoning (131K) - xAI key

*Don't see these? Ask your admin to add API keys in Settings → API Keys*

**Pro Tip:** Start with Gemini Flash for speed or GPT-5 for best results!

## 2. Just Talk Naturally
Type in the message box and hit **Enter**:

**Be specific for best results:**
- ❌ "Find stuff" → Vague
- ✅ "Search my documents for Q3 2025 financial reports" → Clear

**AI figures out which tools to use automatically.** You focus on *what*, AI handles *how*.

**🛠️ Performance tip:** More enabled tools = slower/less context/less accurate in some cases (AI evaluates all options less capable models may go off track). You can keep all tools on—just know the tradeoffs.

## 3. Explore Features
Click sidebar sections (left edge):
- 💬 **Chat** → AI conversations
- 🛠️ **Tools** → Your AI capabilities
- 📄 **Documents** → Search files
- 🧾 **Queues** → Manage workflows
- 📊 **Tables** → Database queries
- 🕸️ **Graph** → Connect knowledge
- 🖼️ **Artifacts** → Interactive apps
- 🔗 **Apps** → Connect GitHub, Slack, etc.

## 4. Key Capabilities
- **🛠️ Comprehensive Tools** → Web search, databases, code execution, research, and more
- **🧠 Memory** → AI learns preferences as you chat. Say "Remember that..." to store explicitly.
- **💬 Full Chat History** → AI can recall past conversations, even across different models
- **📄 Smart Documents** → Upload once, search forever. AI speaks document language (semantic search).
- **📎 Attachments** → Link documents or artifacts to conversations for focused context
- **🔄 Model Switch** → Change AI mid-conversation—chat history persists

## 5. Important to Know

**AI Can Make Mistakes:** Always verify important information. Ask AI to double-check its work.

**Build on Success:** When something works well, reuse that approach for similar tasks.

**Treat AI Like a Colleague:** Knowledgeable and capable, but needs your context and priorities.

## 6. Try These to Get Started

**Explore what Kablewy can do:**
- *"What can you help me with as a [your role]?"*
- *"Give me a tutorial on the web research tools"*
- *"Show me what tools are available and what they do"*

**Get practical help:**
- *"Help me organize information about [your project]"*
- *"What's the best way to track [your task] with Kablewy?"*
- *"Walk me through setting up a workflow for [your process]"*

**Running out of context?** Just switch to a model with a bigger context window (Gemini 2.5 Flash, Grok-4 Fast), use Smart Context or compress your context with the "compress context" button to roll up your current conversation into a summary and keep going!
        `
      },
      'chat': {
        icon: '💬',
        title: 'Chat Interface',
        content: `
# Chat: Your AI Conversation Hub

## The Basics

### Send a Message
1. Type in the text box at the bottom
2. Press **Enter** or click **Send**
3. Watch AI respond in real-time

**Pro Tip:** Just talk naturally. Kablewy figures out what you need.

## Choose Your AI Model

Click the **model selector** (top of chat) to open the model menu.

### 🛠️ Recommended (Always Available)
- **Gemini 2.5 Flash** → Fast, multimodal, huge context (1M) ⭐
- **GPT-5** → Best task completion (slower but thorough) (400K) ⭐
- **Kimi K2** → Ultra-fast, excellent tool use (128K)
- **Grok-4 Fast** → Ultra-fast, massive context (2M)
- **gpt-oss-120b** → Instant responses, great quality (131K)
- **GLM-4.5** → Strong multilingual support

### 🔑 Premium Models (Require API Keys)
- **Gemini 2.5 Pro** → Professional multimodal (1M) - Google key
- **Claude Sonnet 4.5** → Superior writing (200K) - Anthropic key
- **Grok-4** → Advanced reasoning (131K) - xAI key
- **Claude 3.5 Sonnet** → Enhanced writing - Anthropic key

*Don't see these models? Ask your administrator to add API keys in Settings → API Keys*

### COMING SOON! Your Custom Fine-Tuned Models 🔑

### When to Switch:
- **Speed?** → Gemini 2.5 Flash, Kimi K2, Grok-4 Fast
- **Best results?** → GPT-5 (slower but most capable)
- **Multimodal (images)?** → GPT series, Anthropic, Grok or  Gemini models have multimodal capabilities
- **Complex tasks?** → GPT-5, Grok-4, Gemini 2.5 Pro 🔑
- **Writing?** → Claude Sonnet 4.5 🔑, Kimi K2, GPT-5 (with specific prompting)
- **Huge docs (1M+ tokens)?** → GPT-4.1 (1M), Gemini 2.5 Pro 🔑 (1M), Grok-4 Fast (2M)
- **Code?** → GPT-5, Gemini 2.5 Flash, Claue-Sonnet 4.5 🔑, Grok-4-Fast

**🔑 = Requires API key** — Contact your admin if not visible

## Context Window Guide

**Think of it as AI's working memory:**
- 128K tokens ≈ 300 pages
- 400K tokens ≈ 1,000 pages
- 2M tokens ≈ 5,000 pages

**⚠️ Bigger isn't always better!** AI can get "lost in the middle" with massive contexts.

**Better:** Use **Smart Context** to load only relevant information.

## Tools: Your AI Capabilities

Click **🛠️ Tools** to explore what AI can do for you.

**You don't need to know tool names—just ask naturally:**
- *"Search the web for market trends"*
- *"Create a table tracking projects"*
- *"Generate a sunset image"*

AI picks the right capabilities automatically. Think of tools as **specialized skills** AI can use to complete your requests.

## Control Your Conversation

### Cancel Streaming
Click **⏹ Stop** to cancel mid-response.

### Smart Context
Toggle on to let AI automatically pull relevant docs and data.

### Automatic Model Switching
Context overflow? AI seamlessly switches to a bigger model. No errors, just works! 🪄

## Pro Tips

**🎯 Be Specific**
- ❌ "Find stuff"
- ✅ "Search my documents for Q3 financial reports from 2025"
- ❌ ""

**🔗 Chain Commands**
"Search docs, extract themes, create summary table, generate visualization"

**🛠️ Start Fast**
Begin with Gemini 2.5 Flash or Kimi K2. Always available, no keys needed!

**🎯 Be Specific**
More detail = better results. Include context, constraints, and desired format.

**📎 Use Attachments**
Link documents or artifacts to conversations for focused context—like pinning reference materials.

**🧠 Memory Works Two Ways**
- **Automatic:** AI picks up preferences as you chat
- **Explicit:** Say "Remember that I prefer Python" or "Remember to check X before Y"
- **Recall:** Ask "Remember when we talked about the Q3 report?" - AI searches your chat history
- **Cross-Model:** Switch from GPT-5 to Gemini mid-chat—full context carries over!

**✅ Verify Important Info**
AI can make mistakes. Ask it to double-check critical information.

**📋 Build on Success**
When a request works well, reuse that approach for similar tasks.

**🛠️ Tools & Speed**
More enabled tools = slower (AI evaluates more options). Most users keep all tools on—worth it for flexibility!
        `
      },
      'documents': {
        icon: '📄',
        title: 'Documents',
        content: `
# Documents: Permanent Knowledge Infrastructure

Kablewy treats documents as **permanent infrastructure**, not temporary uploads. Index once (or replace), search forever across all your chats and models.

## How It Works

1. **Upload document once** → Permanently stored and indexed
2. **AI processes intelligently** → Extracts text, creates semantic chunks
3. **Available everywhere** → Search from any chat, with any model
4. **Semantic search** → AI understands meaning and context, not just keywords
5. **Compounds over time** → As you add more docs, your knowledge base grows

**The more you upload, the smarter your AI workspace becomes.**

## Upload Documents

1. Navigate to **Documents** in sidebar
2. Click **Upload** (or drag & drop)
3. AI processes and indexes automatically
4. **Now part of your permanent knowledge base**
5. Searchable in all future chats with any model

### Supported Formats:
- PDFs, Word docs (.docx)
- Excel/CSV spreadsheets 
- Powerpoint files (.pptx)
- Text files (.txt, .md)
- And more...

**Think of it as building a library,** not uploading files to a chat.

## Search Documents

### From Any Chat (Recommended):
*"Find all documents mentioning 'machine learning' and summarize them"*
*"Search my budget documents for Q3 2025"*

**Semantic Search:** AI speaks document language—finds concepts and meaning, not just exact word matches.

### From Documents Page:
- Search bar at top
- Results appear instantly
- Click to view details

## Attachments vs Documents

**Documents (📄) - Permanent:**
- Upload once, available forever
- Searchable from any chat
- All models can access
- Semantically indexed

**Attachments (📎) - Focused Context:**
- Link existing documents or upload files to a specific chat
- AI automatically reads attached resources using the read_resource tool
- Attachments stay active for the entire chat session
- Like "pinning" reference materials for this conversation
- Remove attachments when you're done with them to free up context

**When to use which:**
- **Upload to Documents** → For anything you might reference across multiple chats
- **Attach to Chat** → When you want AI to focus on specific files for this conversation

**How Attachments Work:**
When you attach a document, AI sees a notice in your message listing the attached resources. AI then calls the read_resource tool to access the full content. This happens automatically—you just attach and ask questions!

## Pro Tips

**🔍 Semantic Power:** Search "budget issues" finds documents about "financial concerns" too—AI understands meaning.

**📊 Chain Capabilities:** *"Search docs, extract themes, create summary table"*

**🔄 Model-Agnostic:** Upload today with GPT-5, search tomorrow with Gemini, next week with whatever's best—documents persist.

**📎 Focused Context:** Attach 2-3 relevant docs to a chat to help AI prioritize what matters for this conversation.

**🏗️ Compounds Value:** Each document you add makes your entire AI workspace more capable.
        `
      },
      'tools': {
        icon: '🛠️',
        title: 'Tools',
        content: `
# Tools: Your AI Capabilities

Tools are **specialized skills** AI uses to complete your requests—search the web, query databases, generate images, execute code, and much more.

## Viewing Tools

Click the **🛠️ Tools** button in chat to explore what's available:
- Browse capabilities organized by category
- Toggle individual tools on/off as needed
- Search by name or description

## How Tools Work

### No Technical Knowledge Required
Just describe what you need in plain English:

*"Search the web for AI trends"*
→ AI selects and uses web search

*"Create a table to track expenses"*
→ AI builds the table structure

*"Generate an image of a sunset"*
→ AI creates the image

**AI automatically selects the right capabilities** based on your request. You focus on *what* you need, AI handles *how*.

## What You Can Do

### 📄 Document Management
- Upload and semantically search your documents
- Find information across all your files
- Retrieve specific sections from large documents
- Locate files by name or content

### 🔍 Research & Web
- Search the web with AI-powered summaries
- Find news, images, videos, and discussions
- Conduct deep research with citations and sources
- Access academic papers and publications
- Get economic data and market insights

### 📊 Data & Databases
- Create and query SQL tables
- Store and retrieve structured data
- Perform bulk operations and analytics
- Track statistics and generate reports

### 🕸️ Knowledge Connections
- Build knowledge graphs with nodes and relationships
- Connect concepts, documents, and people
- Traverse and visualize your knowledge network
- Find paths between related information

### 🧾 Workflows & Queues
- Create stateful workflows that persist over time
- Track events through multiple stages
- Manage approval processes
- Build human + AI collaboration pipelines

### 🧠 Memory & Context
- AI remembers your preferences automatically
- Store explicit memories for future reference
- Search your conversation history
- Build on previous work across sessions

### 🖼️ Creation & Artifacts
- Generate interactive dashboards and reports
- Create code files, scripts, and configurations
- Build HTML applications and tools
- Produce standalone deliverables

### 🎨 Media Generation
- Generate images from text descriptions
- Create videos with AI
- Design graphics and visualizations

### 💻 Code & Automation
- Execute Python code with full libraries
- Create visualizations and charts
- Process data and perform calculations
- Automate repetitive tasks

### 🗺️ Location & Mapping
- Geocode addresses and places
- Calculate distances and routes
- Search for locations and get details
- Access elevation and geographic data

### 🔗 Integration & Apps
- Manage connected app events (GitHub, Slack, etc.)
- Process notifications and updates
- Sync data across platforms

## Tool Control

### Enable/Disable Tools:
Open Tools panel → Toggle individual tools

**Most users keep everything enabled.** AI only uses what's needed.

### Filter by Search:
Type in the search box: "perplexity", "table", "github"

### Categories Auto-Show:
Only categories with enabled tools appear

### Favorites (Pinned)

Use the ⭐ star next to a tool to **favorite** it:

- Favorites are prioritized when the model receives tools
- Favorites count shows under the Tools header (★ N)
- Click **Favorites On** to enable only your favorites (one click)
- You can favorite up to your model’s tool limit (typically 128). If you try to favorite more, the UI warns and blocks it

> Tip: Favorite your connected apps (e.g., Google Docs, Gmail, Calendar) to ensure they’re always sent first.

### Tool Limit (Typically 128)

Most models can only accept **up to 128** tools per turn. Kablewy:

- Prioritizes favorites and connected apps first
- Sends the first 128 after prioritization
- Shows a banner in the Tools modal when you exceed the limit (with a list of what will be clipped)
- Shows a small warning above the chat input when over the limit, with a link to open Tools

If you need guarantees, use a Context Pack or a per‑turn allowlist (advanced) to force-include a specific set for the next message.

### Reload Tools & Reset Cache

If your tool list looks stale or doesn’t reflect recent changes:

- **Reload Tools**: Re-fetches from the server and updates cache
- **Reset Cache**: Clears the local tools cache, then reloads

Use Reset Cache if you’ve changed integrations, switched accounts, or suspect stale local storage.

## Behind the Scenes

When you ask AI to do something:
1. AI analyzes your request
2. Selects the right capabilities automatically
3. Executes them in sequence
4. Synthesizes results
5. Responds naturally

**You see:** A helpful answer  
**What happened:** AI orchestrated multiple capabilities seamlessly

## Pro Tips

**Let AI Choose at First:** Don't overthink which tools to use—just ask naturally. If the result is not what you want, you can always direct it to use a different tool.

**Be Specific:** More detail = better results. Include context, constraints, and desired format.

**Chain Capabilities:** *"Search docs, create table, generate chart"* → AI chains them automatically

**Tool Hints:** Want something specific? *"Use Perplexity to research..."* or *"Execute Python code to..."*

**Performance:** More enabled tools = slower responses (AI evaluates all options). Most users keep everything enabled—worth it for flexibility!

**Running Low on Context?** Switch to a model with a bigger window (Gemini 2.5 Flash, Grok-4 Fast).
        `
      },
      'tables': {
        icon: '📊',
        title: 'Tables',
        content: `
# Tables: Your Custom Databases

Think of Tables as **spreadsheets on steroids**—powered by SQL and AI. Create structured data storage for anything you need to track.

## What You'll See

When you first visit **Tables**, you'll see:
- **Your tables** (empty at first)
- **Org-wide public tables** (if teammates have shared any)
- **Templates** to get started quickly

Tables are completely custom—you create whatever structure you need.

## Start with Templates

Click **Create from Template** to choose from ready-made structures:

- 👥 **Contact List** - Names, emails, phone numbers
- ✅ **Task Tracker** - Tasks with status, priority, due dates
- 📦 **Inventory Management** - Items, quantities, pricing
- 📅 **Event Log** - Event tracking with timestamps
- 💰 **Financial Tracker** - Income, expenses, transactions
- 👤 **Employee Directory** - Employee information
- 📋 **Project Tracker** - Project progress and deadlines
- 🏢 **Customer Database** - Customer relationships
- 🎫 **Support Tickets** - Customer support tracking
- 🔧 **Equipment Registry** - Equipment and maintenance
- 💼 **Sales Leads** - Lead tracking and opportunities
- 🎓 **Training Records** - Employee training and certifications

Each template includes sample data to show you how it works.

## Create Custom Tables

### From Chat (Easy Way):
*"Create a table called 'research_papers' with columns: title, author, publication_date, summary, tags"*

AI builds it for you with the right column types.

### Manual Way:
1. Go to **Tables**
2. Click **Create Table**
3. Name your table
4. Define columns (name, type, nullable)
5. Click **Save**

## Share Tables Across Your Org

Like documents and graph nodes, tables can be **public** or **private**:

- **Private** (default) - Only you can see and edit
- **Public** - Everyone in your org can see it, only you can edit

**Use cases for public tables:**
- Shared reference data (product catalogs, price lists)
- Team resources (training schedules, equipment lists)
- Collaborative tracking (project status, customer info)
- Organization-wide knowledge bases

To make a table public, toggle its visibility from the table options menu.

## Query Tables

### From Chat (Recommended):
- *"Find my projects table and show me all rows with status 'in progress'"*
- *"Query my customers table and find all rows with account_value over $50,000"*
- *"Search my support tickets table for tickets created this week, sorted by priority"*

AI writes the SQL for you automatically.

### Direct SQL:
1. Go to **Tables**
2. Select a table
3. Click **Query**
4. Write SQL
5. Run it

## Update Data

### From Chat:
- *"Add a new row to my projects table: Website Redesign, status In Progress, due 2025-03-15"*
- *"Find my customers table and update it: set status to 'Active' where customer_id is 'CUST001'"*
- *"Delete all completed tasks from my task tracker table"*

### Manual:
- Click **Add Row** to insert data
- Click any row to view/edit details
- Use bulk operations for multiple rows

## Pro Tips

**📋 Start with Templates**
Browse templates even if you modify them—they show best practices for column naming and types.

**🌐 Share Strategic Data**
Make reference tables public so your team has a single source of truth. No more outdated spreadsheets!

**🔗 Join Tables with AI**
*"Find my projects table and employees table, then show me all projects assigned to employees in the Engineering department"* - AI handles complex joins.

**📈 Aggregate & Analyze**
*"Query my financial tracker table and calculate total revenue by month, then create a chart"*

**💾 Export Anytime**
*"Export my customers table as CSV"* or download directly from the table view.

**🔄 Iterate Quickly**
Tables are flexible—add columns, modify types, and restructure as your needs evolve.

**👀 See What Others Built**
Check public tables from teammates—they might have exactly what you need!
        `
      },
      'graph': {
        icon: '🕸️',
        title: 'Knowledge Graph',
        content: `
# Graph: Your Knowledge Network

The **Graph** view lets you build and explore connections between ideas, documents, people, projects—anything.

## What is a Knowledge Graph?

Imagine a mind map that AI can navigate:
- **Nodes** → Things (documents, people, concepts,memories)
- **Relationships** → Connections (authored by, relates to, depends on)

## Create Nodes

### From Chat:
*"Create a node for 'Q3 Budget Report' and connect it to 'Finance Team'"*

### Manual:
1. Go to **Graph**
2. Click **Create Node**
3. Set type, name, properties
4. Click **Save**

## Create Relationships

### From Chat:
*"Link the 'Machine Learning' node to 'AI Strategy' with relationship 'supports'"*

### Manual:
1. Select two nodes
2. Click **Create Relationship**
3. Define type (e.g., "depends on", "authored by")
4. Click **Save**

## Explore the Graph

### From Chat:
*"Show me everything connected to 'Q3 Budget Report'"*
*"Find the path from 'Customer Feedback' to 'Product Roadmap'"*

AI traverses the graph and explains connections.

## Memory Nodes (Special Type)

Create **MEMORY nodes** to store important facts AI should remember:

*"Remember that our fiscal year starts in July"*
*"Create a memory: I prefer Python over JavaScript for scripting"*

These become **searchable knowledge** AI uses automatically.

## Use Cases

**📚 Research:** Connect papers, authors, concepts
**📁 Project Management:** Link tasks, dependencies, owners
**🧩 Knowledge Base:** Map company knowledge and expertise
**🔍 Discovery:** Find hidden connections in your data
**🧠 Long-term Memory:** Store facts AI should always remember
        `
      },
      'memory': {
        icon: '🧠',
        title: 'Memory System',
        content: `
# Memory: AI That Remembers You

Kablewy has a **two-layer memory system** that makes AI remember your preferences, projects, skills, and insights.

## How Memory Works

### Layer 1: Background Memory (Automatic)
AI automatically learns from your conversations:

**You say:** *"I prefer Python for data analysis"*  
**AI learns:** Stores as a preference memory

**You say:** *"Remember that our fiscal year starts in July"*  
**AI learns:** Stores as an important fact

**You say:** *"I'm working on a machine learning project for healthcare"*  
**AI learns:** Stores as a project memory

### Layer 2: Knowledge Graph Memories (Explicit)
Create **MEMORY nodes** in the knowledge graph for important facts:

*"Create a memory node: Company policy requires approval for expenses over $5,000"*

These nodes:
- Live in the graph alongside other knowledge
- Connect to related concepts
- Persist forever unless deleted
- Are searchable when relevant

## What AI Remembers

- 🙋 **Personal:** Your role, timezone, contact preferences
- ⚙️ **Preferences:** Tools, working styles, communication preferences  
- 📁 **Projects:** Current and past projects, team context, goals
- 🎓 **Skills:** Technical expertise, domain knowledge
- 💡 **Research Insights:** Facts discovered, patterns observed, lessons learned

## How AI Uses Memory

**During conversations:**
1. AI reads your query
2. Searches for relevant memories automatically
3. Uses them to personalize the response

**Example:**
- **You:** *"Help me with data analysis"*
- **AI remembers:** Your Python preference, healthcare project, SQL skills
- **AI responds:** With Python examples tailored to healthcare and SQL

**Silent and automatic—no loading messages, just better responses.**

## Managing Your Memories

### Create:
*"Remember that I prefer TypeScript"*  
*"Create a memory node: Q3 budget requires VP approval"*

### View:
*"Show me what you remember about me"*  
*"List my memory nodes"*

### Update:
*"Update: I now prefer Markdown over HTML"*

### Remove:
*"Forget my preference about detailed comments"*  
*"Delete the memory about Tuesday meetings"*

## Memory + Graph = Powerful

**Create connected knowledge:**

*"Create a memory: We use GitHub Actions for CI/CD"*

*"Link that memory to my 'DevOps Automation' project node"*

**Later, AI uses it automatically:**

*"Help me set up deployment automation"*  
→ AI already knows you use GitHub Actions!

## Privacy & Control

- ✅ **Your data only** - Memories never shared across users
- ✅ **You control it** - Add, view, update, delete anytime
- ✅ **Encrypted** - All memory data encrypted at rest
- ✅ **Opt-out** - Can disable memory features if desired

## Pro Tips

**🎯 Be Explicit When It Matters**
*"Remember that..."* or *"Remember to..."* ensures important facts are captured.

**🔗 Use Graph Memories for Complex Knowledge**
When information needs to connect to other concepts, use MEMORY nodes in the graph.

**💬 Recall Past Conversations**
*"Remember when we discussed the Q3 report?"* searches your full chat history.

**🔄 Cross-Model Memory**
Switch from GPT-5 to Gemini—AI still remembers everything!

**🧹 Keep Memories Relevant**
Update or delete outdated information periodically.
        `
      },
      'media': {
        icon: '🎨',
        title: 'Media Generation',
        content: `
# Media: Create & Refine Visual Content

Transform ideas into professional images and videos—then edit them with precision until they're exactly right.

## The Editing Revolution

What makes modern AI media generation powerful isn't just creation—it's **precise editing**. Make targeted changes without starting over:

- Change a product's color from blue to red
- Swap backgrounds while keeping your subject
- Adjust lighting, add elements, or remove distractions
- Iterate rapidly until you nail the perfect result

**This changes everything for businesses:** No more endless regeneration cycles. No more "almost perfect but..." frustration. Just create, refine, ship.

## Business Applications

### 🎯 Marketing & Advertising
Generate campaign visuals and refine them in real-time during creative reviews. Test multiple variations instantly:
- Product shots in different settings
- Social media content adapted for each platform
- A/B test visuals before committing to expensive photoshoots

### 📦 Product Visualization
Show products that don't exist yet—and iterate based on stakeholder feedback:
- Visualize concepts before manufacturing
- Create catalog imagery without physical samples
- Test packaging designs in various environments

### 🎓 Training & Education
Build custom training materials on demand:
- Scenario simulations for safety training
- Product demonstrations and tutorials
- Localized content for different markets

### 🏢 Brand Development
Maintain visual consistency across everything:
- Generate brand-compliant imagery at scale
- Create cohesive visual narratives
- Adapt existing brand assets to new contexts

### 🎬 Video Content
Create compelling video content without production crews:
- Product demos and explainer videos
- Social media clips and reels
- Internal communications and announcements

## How to Use

### Generate Images:
*"Create a professional product photo of a smartwatch on a minimalist desk with morning light"*

Then refine it:
*"Make the lighting warmer"*
*"Change the watch band to leather"*
*"Add a coffee cup in the background"*

### Generate Videos (In Chat Only):
*"Create a 5-second video showing ocean waves at golden hour with seabirds flying"*

Then perfect it:
*"Make the waves more dramatic"*
*"Change the lighting to sunset"*

### Video Tools (Unified)

Kablewy provides a single, unified video tool: **\`generate_video\`**.

- **Defaults to Veo 3 (with audio, 16:9)** for simple prompts.
- **Automatically routes to Veo 2** when you include Veo‑2 options:
  - \`durationSeconds\` (5–8 seconds)
  - \`numberOfVideos\` (1 or 2)
  - \`aspectRatio: "9:16"\`
  - \`personGeneration\`, \`enhancePrompt\`

#### Tips
- Keep prompts specific (subject, motion, style, composition).
- After generation, find all videos under **Media → Videos**.
- Video links are served via short‑lived presigned URLs and **auto‑refresh** when opened, so they won’t unexpectedly expire in the UI.
- Use the video card actions to open, copy a fresh link, or delete.

### From the Media Page:
1. Navigate to **Media**
2. Use the **Generate** tab for new image creations
3. View your **Gallery** to see all generated media
4. Click any item to view, download, or refine further

## Pro Tips

**🎯 Start Specific, Refine Precisely**
Create with detail, then make targeted edits. Each change happens exactly where you want it—no surprises.

**💼 Build Your Asset Library**
Generated media lives in your gallery forever. Build a collection of brand-compliant assets you can iterate on anytime.

**🛠️ Rapid Iteration = Better Results**
Don't settle for "close enough." Make 5-10 quick refinements to get exactly what you need.

**🎨 Style Consistency**
Once you nail a look, describe it precisely so you can recreate it: *"Match the lighting and color grade from [previous image]"*

**📐 Think in Layers**
Complex scenes? Build them incrementally: create the base scene, add elements, adjust lighting, refine details.

**🔄 Test Variations Fast**
Generate a base, then create 3-4 variations with targeted changes. See what works best before committing.
        `
      },
      'queues': {
        icon: '🧾',
        title: 'Queues & Workflows',
        content: `
# Queues: Stateful Workflows That Evolve

Think of Queues as **smart to-do lists that remember everything**—perfect for complex processes that unfold over time.

## Why Queues Are Awesome

### 🎯 Context That Doesn't Disappear
Unlike chat messages that scroll away, queues maintain **persistent state**:
- Track document review from draft → review → approved
- Monitor deal pipeline from lead → negotiation → closed
- Manage support tickets from new → assigned → resolved

### 🤝 Human + AI Collaboration
Perfect middle ground between "AI does everything" and "I do everything":
- **AI processes** → Categorizes, summarizes, analyzes
- **You decide** → Approve, reject, provide input
- **AI executes** → Updates, notifies, creates next steps

### 📊 Complex Workflows Made Simple
Break big, messy processes into clear stages:
1. **Intake** → New items arrive
2. **Validation** → AI checks completeness
3. **Processing** → AI or human takes action
4. **Review** → Human approves/rejects
5. **Completion** → Archive and notify

## Creating a Queue

### From Chat (Easy):
*"Create a queue for customer support tickets with stages: new, assigned, in-progress, resolved"*

### Using Templates:
*"Create a sales pipeline queue using the template"*

Pre-built templates:
- 📞 Customer Support
- 💼 Sales Pipeline  
- 🏡 Real Estate Transactions
- 📝 Content Workflow
- 📄 Invoice Processing

## Adding Events

### Manually from Chat:
*"Add a new event to the support queue: Customer Jane Doe reported login issue"*

### Automatically from Integrations:
Connect GitHub, Slack, etc. → Events flow in automatically → AI categorizes them

## Managing Your Queue

### Check Status:
*"How many events are in the support queue?"*
*"Show me all events stuck in review stage"*

### Move Things Forward:
*"Move ticket #123 to resolved stage"*
*"Process the next 5 events in the invoice queue"*

### Batch Operations:
*"Update all events from last week that are still pending to 'needs attention'"*

## Real-World Example

**Document Review Pipeline:**
1. PDFs uploaded → Auto-added to queue at "intake" stage
2. AI extracts key info → Moves to "extracted" stage
3. AI validates completeness → Moves to "ready for review"
4. You review in batch → Approve/reject
5. AI files approved docs → Moves to "archived"

All context preserved. All history tracked. Zero chaos.
        `
      },
      'artifacts': {
        icon: '🖼️',
        title: 'Artifacts',
        content: `
# Artifacts: Living Documents & Interactive Apps

Artifacts are **standalone deliverables** that AI creates for you—dashboards, reports, apps, code—all living in their own windows.

## What Are Artifacts?

Think of artifacts as **things you can open, share, and reuse**:
- 📊 Interactive dashboards with live data
- 📄 Formatted reports and documentation  
- 🎨 SVG graphics and diagrams
- 💻 Complete code files and scripts
- 🖼️ HTML applications and tools

**Key Difference:** Chat messages disappear. Artifacts persist.

## Creating Artifacts

### Just Ask AI:
*"Create an interactive dashboard showing my project status"*
*"Make a Python script to analyze CSV files"*
*"Generate an HTML report of this quarter's performance"*

AI automatically creates an artifact when your request needs:
- Interactive elements (buttons, forms, charts)
- Substantial documentation (reports, guides)
- Code that should be saved as a file
- Graphics that need precise rendering

### They Open in Floating Windows
Artifacts pop up alongside your chat—you can:
- Resize and reposition them
- Copy content or download
- Edit and ask AI to update them
- Share the link with others

## Types of Artifacts

### 🌐 HTML Applications
Interactive tools built on-the-fly:
- Data visualization dashboards
- Form builders and calculators
- Custom interfaces
- Mini web apps

*"Create an expense tracker app with category breakdown"*

### 📝 Markdown Documentation
Beautifully formatted docs:
- Technical specifications
- Process documentation  
- Project plans
- Meeting notes

*"Write a technical spec for our API and render it as a document"*

### 🎨 SVG Graphics
Scalable vector images:
- Architecture diagrams
- Flowcharts and org charts
- Custom illustrations
- Infographics

*"Create an SVG flowchart showing our deployment process"*

### 💻 Code Files
Complete, runnable scripts:
- Python, JavaScript, TypeScript
- Shell scripts, SQL queries
- Configuration files (YAML, JSON)
- Any programming language

*"Write a Python script to merge CSV files and save it as an artifact"*

## Working with Artifacts

### Browse Your Artifacts:
*"List all my artifacts"*
*"Find artifacts about 'customer analysis'"*

### Open Previous Work:
*"Open the dashboard artifact I created yesterday"*

### Edit and Iterate:
*"Update the expense tracker to include a monthly summary"*
→ AI creates a new version, preserves the old one

### Share with Others:
Every artifact gets a **secure link** (expires in 24 hours by default):
- Copy the link from the artifact window
- Share via email, Slack, etc.
- Recipients can view without logging in

### Version History:
Artifacts are automatically versioned—you can:
- See all previous versions
- Restore an older version
- Compare changes over time

## Pro Tips

**🎯 Request Artifacts Explicitly**
When you want something substantial:
*"Create this as an artifact"* or *"Make an interactive dashboard"*

**🔄 Iterate Freely**
*"Make it bigger"*
*"Add a dark mode toggle"*
*"Include last month's data too"*

AI updates the artifact, keeps the old version.

**📎 Attach to Future Chats**
Reference artifacts later:
*"Use that dashboard artifact to create a presentation"*

**🛠️ Everything Auto-Saves**
Artifacts are stored securely in the cloud. No manual saving needed.

## Examples That Work Great

### Business Reports
*"Create an executive dashboard showing: revenue, expenses, profit margin, and key metrics"*

### Code Generation
*"Write a complete REST API in TypeScript with authentication and CRUD operations"*

### Documentation
*"Generate API documentation from this code and format it as a beautiful artifact"*

### Data Visualizations
*"Create an interactive chart showing sales by region with filters and drill-down"*

### Print-Ready PDFs
*"Create an HTML artifact with print styles optimized for PDF—include a 'Print to PDF' button"*

Then click the button in the artifact window → Print → Save as PDF
        `
      },
      'integrations': {
        icon: '🔗',
        title: 'Connecting Your Apps',
        content: `
# Connecting Your Apps: Bring Everything Together

Connect Slack, GitHub, Google Drive, Salesforce—**2,700+ apps** via Pipedream—so AI can help you manage everything in one place.

**Access:** Navigate to **Settings → Integrations**

## The Complete Picture

### Step 1: Connect an App
1. Go to **Settings** → **Integrations** → **Connected Accounts**
2. Search for an app (Gmail, Slack, GitHub, Google Sheets, etc.)
3. Click **Connect**
4. Authorize via OAuth (secure, no password sharing)
5. Account is now connected

### Step 2: Events Flow to Your Inbox
When something happens in the connected app (new email, GitHub issue, Slack message), it's stored in your **Connected App Inbox**.

### Step 3: Choose Your Processing Path

#### **🤖 AI-Powered Processing (Recommended for Getting Started)**

Ask AI to process events from chat:

*"Check my connected app inbox"*
→ Shows recent events from all connected apps

*"Process my inbox events from this week"*
→ AI goes through them systematically:
- Groups by source (GitHub, Slack, Google)
- Categorizes by type and priority
- Summarizes key information
- Extracts action items
- Suggests next steps

*"Find GitHub issues in my inbox and create a table tracking them"*
→ AI processes events and creates structured data

*"Process and delete the 10 oldest events"*
→ AI handles cleanup automatically

**Why this works:** AI understands context, categorizes intelligently, and can take any action (create documents, update tables, add to queues, etc.) based on what makes sense.

#### **🛠️ Automated Workflow Jobs (Advanced)**

For high-volume or time-critical workflows, configure automatic routing:

1. Go to **Settings** → **Integrations** → **Connected App Workflows**
2. Click **Create Workflow**
3. Select connected account
4. Choose trigger (what event starts the workflow)
5. Configure routing rules:
   - If email subject contains "urgent" → Add to Priority Queue
   - If GitHub label is "bug" → Add to Bug Tracker table
   - If Slack channel is "support" → Create document + alert team

**What it does:** Events matching your rules automatically route to tables, queues, graphs, or documents—no AI processing needed.

**When to use:** High-volume data flows where you want instant, deterministic routing.

## Connected App Inbox: Your Command Center

### Check Inbox Size:
*"How many events do I have?"*
→ Quick count without loading everything (default: past 7 days)

### Process Systematically:
*"Go through my inbox 20 events at a time and categorize by source"*
→ AI paginates automatically (processes oldest first)

### Filter and Focus:
*"Show me just GitHub events from the past 3 days"*
*"List Slack messages that mention 'deadline'"*

### Smart Cleanup:
*"Process my inbox and delete events after summarizing them"*
→ AI processes, extracts value, then cleans up

### Cross-App Intelligence:
*"Find all Gmail threads related to the project tracked in my GitHub issues"*
→ AI connects data across apps

## Real-World Scenarios

### 📧 Critical Email Monitoring
- Gmail connected → Important emails flow to inbox
- Ask AI: *"Check inbox for client emails, flag urgent items"*
- AI identifies urgency, summarizes threads, suggests responses
- Optionally: Workflow job auto-routes VIP clients to priority queue

### 💻 GitHub Workflow
- GitHub connected → Issues and PRs arrive
- Ask AI: *"Process GitHub events, create a prioritized review list"*
- AI categorizes by label, urgency, and complexity
- Optionally: Workflow job auto-adds bugs to Bug Tracker table

### 📊 Data Sync
- Google Sheets connected → New rows trigger events
- Ask AI: *"Process spreadsheet events and sync to my sales_leads table"*
- AI maps fields, validates data, inserts rows
- Optionally: Workflow job auto-syncs matching rules

### 💬 Communication Hub
- Slack connected → @mentions and keywords tracked
- Ask AI: *"Summarize my Slack mentions and create action items"*
- AI prioritizes, extracts tasks, suggests responses
- Optionally: Workflow job routes support requests to support queue

## Pro Tips

**📊 Start with Inbox + AI**
Simple, flexible, and AI helps you figure out what to do with events.

**🛠️ Upgrade to Workflow Jobs**
Once patterns emerge, automate the repetitive routing with rules.

**🔍 Use Count-Only First**
Before processing 1000 events, check count: *"How many events do I have?"*

**🎯 Filter by Source**
Process one app at a time: *"Show me just Gmail events"*

**🧹 Clean as You Go**
Enable delete_after when processing: AI extracts value, then removes noise.

**📦 Think Batch, Not Real-Time**
Process inbox daily/weekly in batches—more efficient than real-time monitoring.

**🔗 Connect High-Signal Apps First**
Start with apps that generate actionable events (email, project management, support tickets).
        `
      },
      'settings': {
        icon: '⚙️',
        title: 'Settings',
        content: `
# Settings: Configure Your Workspace

## Organization Settings

Navigate to **Settings** → **Organization**:
- **Organization Name** → Your workspace name
- **Features** → Enable/disable major features
- **Branding** → Customize colors and logo
- **Security** → MFA and session settings

## Tools Configuration

Click the **🛠️ Tools** button in the chat interface to:
- Enable/disable specific capabilities
- Browse all available tools by category
- Configure tool permissions
- Search for specific capabilities

**Pro Tip:** Most users keep all tools enabled. AI only uses what it needs.

## API Keys (BYOK - Bring Your Own Key)

Click the **API Keys** tab to add your own keys:
- **OpenAI** → GPT models
- **Anthropic** → Claude models
- **Google** → Gemini models  
- **Perplexity** → Web search & research
- **Custom integrations**

### Why BYOK?
- Use your existing API credits
- No markup or middleman costs
- Full transparency on usage
- Your data, your keys, your control

**Security:** All keys are encrypted at rest and in transit. Never exposed in logs or responses.
        `
      },
      'workflows': {
        icon: '🎯',
        title: 'Common Workflows',
        content: `
# Common Workflows

## Research & Summarization

**Goal:** Research a topic and create a summary

1. **Chat:** *"Use Perplexity to research the latest AI safety developments"*
2. Wait for AI to search and compile results
3. **Follow-up:** *"Summarize the key findings in bullet points"*
4. **Save:** *"Create a document called 'AI Safety Research Summary' with this content"*

## Document Analysis

**Goal:** Extract insights from uploaded files

1. **Upload:** Go to **Documents** → Upload your files
2. **Chat:** *"Analyze all documents tagged 'customer feedback' and identify common themes"*
3. **Visualize:** *"Create a table showing theme, frequency, and example quotes"*
4. **Act:** *"Generate action items based on the top 3 themes"*

## Data Management

**Goal:** Track projects in a database

1. **Create:** *"Create a table called 'projects' with columns: name, status, owner, deadline, budget"*
2. **Populate:** *"Add 5 sample projects to the table"*
3. **Query:** *"Show me all projects with status 'at risk' and deadline before next month"*
4. **Update:** *"Change the status of 'Website Redesign' to 'completed'"*

## Creative Content

**Goal:** Generate marketing visuals

1. **Brainstorm:** *"Give me 5 ideas for social media images promoting our new AI product"*
2. **Generate:** *"Create an image for idea #3: futuristic AI assistant helping a business team"*
3. **Refine:** *"Make it brighter and add our brand colors (blue and orange)"*
4. **Download:** Go to **Media** → **Gallery** → Download

## Knowledge Building

**Goal:** Build a knowledge graph of your research

1. **Create Nodes:** *"Create nodes for: Machine Learning, Deep Learning, Neural Networks, AI Ethics"*
2. **Connect:** *"Link Deep Learning to Machine Learning with relationship 'is a subset of'"*
3. **Expand:** *"Add nodes for key researchers in each area and connect them"*
4. **Explore:** *"Show me the shortest path from AI Ethics to Neural Networks"*
        `
      },
      'prompting': {
        icon: '✍️',
        title: 'Prompting Tips',
        content: `
# Get Better AI Results: Prompting Essentials

The difference between frustrating AI and magical AI? How you ask. Here's how to get consistently great results.

## The Golden Rule: Be Specific

**Bad:** *"Help me with marketing"*  
**Good:** *"Create a 3-post LinkedIn content calendar for this week about AI productivity tools, targeting startup founders, professional but conversational tone"*

**Why it works:** AI responds to exactly what you write, not what you mean. Specificity eliminates guesswork.

## The CLEAR Framework

Every great prompt includes:

**C - Context:** What's the situation?  
**L - Length:** How long should the response be?  
**E - Examples:** Show what you want (if helpful)  
**A - Audience:** Who is this for?  
**R - Role:** What expertise should AI bring?

### CLEAR in Action:

**Without CLEAR:**
*"Write an email"*

**With CLEAR:**
*"You're a customer success manager (R). Write a 3-paragraph email (L) to a client who's been inactive for 30 days (C), encouraging them to re-engage with our platform. Use a friendly but professional tone suitable for B2B relationships (A). Here's an example structure: Welcome back → Show them what they're missing → Easy next step (E)."*

## Essential Prompting Patterns

### 1. Set the Role

Tell AI what expert perspective to use:

- *"You're a senior data analyst..."*
- *"As a marketing strategist..."*
- *"Acting as a technical writer..."*

**Why:** Different roles = different expertise and tone.

### 2. Show, Don't Just Tell

Give examples of what you want:

*"Create product descriptions. Example: 'Transform your workspace with ergonomic design that actually works—12-hour comfort guaranteed or your money back.' Now write similar descriptions for these products: [list]"*

### 3. Define the Format

Be exact about structure:

- "3 bullet points, one sentence each"
- "Table with columns: Feature, Benefit, Use Case"
- "5 paragraphs: intro, 3 body, conclusion"
- "JSON format with these fields: [specify]"

### 4. Chain Complex Tasks

Break big requests into steps:

**Instead of:** *"Create a complete marketing strategy"*

**Do this:**
1. *"Research current trends in [industry]"*
2. *"Based on that research, identify our top 3 competitive advantages"*
3. *"Now create messaging that emphasizes those advantages"*
4. *"Finally, design a 3-month campaign using that messaging"*

### 5. Iterate to Perfect

Never settle for the first response:

**Try 1:** *"Write a tagline"*  
**Try 2:** *"Write a tagline for a sustainable fashion brand"*  
**Try 3:** *"Write a tagline for a sustainable fashion brand targeting Gen Z, emphasizing authenticity and environmental impact, under 10 words"*

Each iteration gets better because it's more specific.

## Common Mistakes to Avoid

### ❌ Too Vague
"Help me with my presentation"  
**Fix:** "Create a 10-slide outline for presenting Q4 sales results to executives, focusing on growth metrics and future projections"

### ❌ Information Overload
Pasting your entire company history when asking for a tagline  
**Fix:** Include only directly relevant information

### ❌ Assuming AI Knows Your Context
"Write the usual report"  
**Fix:** "Write a weekly sales report with these sections: Summary, Top Deals, Pipeline Changes, Next Week's Focus"

### ❌ No Success Criteria
"Make it good"  
**Fix:** "Make it professional, 200 words max, include 2-3 statistics, end with clear call-to-action"

## Advanced Patterns

### Ask AI to Show Its Work

For complex analysis, request step-by-step reasoning:

*"Analyze whether we should hire 2 junior developers or 1 senior developer. Think through this step-by-step:  
Step 1: Calculate cost implications  
Step 2: Assess skill gaps each option fills  
Step 3: Consider management overhead  
Step 4: Evaluate long-term team development  
Step 5: Provide recommendation with reasoning"*

### Use Constraints to Guide Output

Tell AI what NOT to do:

*"Explain machine learning to business executives. Avoid technical jargon. Don't use analogies about 'training models' or 'neural networks.' Focus on business value and practical applications."*

### Request Multiple Perspectives

*"Evaluate this business decision from three viewpoints:  
1. As a financial analyst - focus on ROI  
2. As an operations expert - assess feasibility  
3. As a marketing strategist - consider market impact  
Then synthesize into a balanced recommendation."*

## Domain-Specific Tips

### For Analysis Tasks
- Request specific frameworks: "Use SWOT analysis"
- Ask for data: "Include relevant statistics"
- Demand structure: "Prioritized by impact"

### For Creative Work
- Set the tone explicitly: "Conversational but professional"
- Provide style references: "In the style of [example]"
- Control length precisely: "Exactly 280 characters for a tweet"

### For Technical Tasks
- Specify expertise level: "Explain to a beginner" vs. "Assume advanced knowledge"
- Request code standards: "Follow PEP 8 style" or "Use TypeScript with JSDoc"
- Ask for edge cases: "Include error handling for these scenarios: [list]"

## Pro Techniques

### The Template Approach

Create reusable prompt templates:

*"[ROLE] You're an expert [domain specialist].  
[TASK] [Specific action needed].  
[CONTEXT] [Background information].  
[FORMAT] Provide output as [structure].  
[CONSTRAINTS] Avoid [limitations].  
[EXAMPLE] Like this: [sample]."*

Save these templates, fill in the brackets for each use.

### Conditional Logic

Build decision trees:

*"IF the customer issue is technical, provide troubleshooting steps. IF it's billing-related, explain our refund policy. IF it's a feature request, acknowledge it and explain our roadmap process. Customer message: [message]"*

### Constitutional Rules

Set global guidelines:

*"For all responses, follow these principles:  
- Focus on actionable advice, not theory  
- Use specific examples, not generalizations  
- Be encouraging without being patronizing  
- Cite sources when making factual claims  
Now: [your actual request]"*

## Troubleshooting Prompts

### AI Gives Generic Results?
**Add:** Unique angle, specific constraints, uncommon perspectives

*Instead of:* "Productivity tips"  
*Try:* "Productivity strategies for creative professionals who hate rigid schedules—focus on working with energy cycles, not against them. No advice about waking up early or time blocking."*

### AI Misunderstands You?
**Checklist:**
- Are pronouns clear? ("their feedback" → "the client's feedback")
- Did you define acronyms?
- Is the audience specified?
- Are there conflicting instructions?

### Output Too Long or Short?
**Be exact:** Not "keep it concise" → "Maximum 150 words, 3 paragraphs"

### AI Refuses a Valid Request?
**Reframe educationally:**  
*Instead of:* "Write a phishing email"  
*Try:* "Create an educational example of a phishing email for security training, clearly labeled as a demonstration of what to avoid"*

## Quick Wins

### 5-Minute Improvements

1. **Add Context:** Every prompt → add 1-2 sentences of background
2. **Specify Length:** Always include word/paragraph count
3. **Name the Audience:** Who will read/use this?
4. **Request Format:** How should it be structured?
5. **Set the Tone:** Formal? Casual? Technical?

### The 3-Try Rule

- Try 1: Initial prompt
- Try 2: Add specifics based on what was missing
- Try 3: Fine-tune format, tone, and examples

Each iteration gets 30-40% better. Always try 3 times before giving up.

## Examples That Teach

### Business Email

**Basic:** *"Write an email to my team about the deadline"*

**Better:** *"Write a 2-paragraph email to my product team about moving the launch deadline from March 15 to April 1. Acknowledge the inconvenience, explain it's to ensure quality, and outline the revised timeline. Professional but empathetic tone."*

### Data Analysis

**Basic:** *"Analyze this data"*

**Better:** *"Analyze this sales data for Q4. Focus on: 1) Revenue trends by region, 2) Top 3 performing products, 3) Notable customer segments. Create a table summarizing findings and provide 2-3 actionable recommendations for Q1 strategy."*

### Creative Content

**Basic:** *"Write a social media post"*

**Better:** *"Write a LinkedIn post (200 words) about AI transforming marketing. Target audience: CMOs at mid-size companies. Tone: thought-provoking but practical. Include: 1 bold statement, 2-3 insights, 1 question to drive engagement. No hype or buzzwords."*

## Remember

**🎯 Clarity > Cleverness:** Simple, specific prompts beat elaborate ones  
**🔄 Iterate:** First response is a draft, refine it  
**📋 Templates:** Save what works, reuse it  
**🎓 Learn Patterns:** Notice what gets good results, do more of that  
**💬 Talk to AI Like a Colleague:** Knowledgeable but needs your context

The best prompters aren't the most technical—they're the most clear about what they want.
        `
      },
      'tips': {
        icon: '💡',
        title: 'Power User Tips',
        content: `
# Power User Tips

## Batch Operations

### Multiple Documents:
*"Search all documents uploaded in October for mentions of 'budget' and create a summary table"*

### Bulk Data:
*"For each project in the 'projects' table, search documents for related files and add a 'documents' column with the count"*

## Advanced Tool Chaining

**Research → Analyze → Visualize → Report:**

\`\`\`
"Use Parallel search to research AI market trends, 
then search my documents for our internal AI strategy, 
compare the external trends to our strategy, 
create a table showing gaps and opportunities, 
generate an artifact using performant chart.js and tailwind css visualizing the market landscape.
Write a 2-page executive summary using markdown as your final output."
\`\`\`

AI orchestrates all steps automatically.

## Context Engineering

### Attach Relevant Resources:
Before asking a complex question, attach:
- Key documents
- Relevant tables
- Previous chat summaries

### Use Smart Context:
Toggle it on to let AI automatically pull in relevant context.

### Chunk Large Tasks:
Instead of one massive prompt, break it into steps:
1. *"First, research X"*
2. *"Now analyze Y based on that research"*
3. *"Finally, create Z"*

## Model Selection Strategy

### 🛠️ Start Fast, Scale When Needed

**Always Available (No Setup Required):**
- **Gemini 2.5 Flash** → Fast, multimodal, excellent tool use (1M) ⭐
- **GPT-5** → Best task completion (slower but most capable) (400K) ⭐
- **Grok-4 Fast** → Ultra-fast, massive context (2M)
- **Kimi K2** → Ultra-fast, reliable, strong tool use (128K)
- **gpt-oss-120b** → Instant responses, great quality (131K)
- **GLM-4.5** → Excellent multilingual, great tool use

**Premium Models (API Keys Required):**
- **Gemini 2.5 Pro** 🔑 → Professional multimodal (1M) - Google key
- **Claude Sonnet 4.5** 🔑 → Superior writing (200K) - Anthropic key
- **Grok-4** 🔑 → Advanced reasoning (131K) - xAI key
- **GPT-5 Mini** 🔑 → Thorough analysis (400K) - OpenAI key
- **GPT-4.1** 🔑 → Powerful tool use, huge context (1M) - OpenAI key

*Don't see premium models? Contact your administrator to add API keys.*

### 🧠 Context Window Matters

**Think of context as AI's "working memory":**
- 128K tokens ≈ 300 pages of text
- 400K tokens ≈ 1,000 pages
- 2M tokens ≈ 5,000 pages

**Match Context to Task:**
- **Short tasks** (quick questions) → Any model works
- **Medium context** (few docs) → 128-200K (Kimi, Claude)
- **Large context** (many docs) → 400K+ (GPT-5, GPT-4.1)
- **Massive context** (entire codebases) → 1-2M (Grok, Gemini, GPT-4.1)

**⚠️ Bigger Isn't Always Better:**
Models can get "lost in the middle" with huge contexts. If you stuff 500 pages in, AI might miss details buried in the middle.

**Better Strategy:** Use **Smart Context** or attach only relevant docs.

### 🎯 Match Model to Task

| Task | Best Model | Why | Key Needed? |
|------|-----------|-----|-------------|
| **Quick Questions** | Gemini 2.5 Flash, Kimi K2 | Instant + huge context | No |
| **Best Results** | GPT-5 | Most capable (worth the wait) | No |
| **Multimodal** | Gemini 2.5 Flash/Pro | Best vision/audio | Flash: No, Pro: Yes 🔑 |
| **Research** | GPT-5, Gemini 2.5 Pro | Top task completion | Pro: Yes 🔑 |
| **Writing** | Claude Sonnet 4.5, GPT-5 | Best prose | Claude: Yes 🔑 |
| **Code** | GPT-5, Gemini 2.5 Flash | Strong reasoning + tools | No |
| **Math/Logic** | GPT-5, Gemini 2.5 Pro | Highest accuracy | Pro: Yes 🔑 |
| **Speed** | Gemini 2.5 Flash, Grok-4 Fast | Sub-second responses | No |
| **Huge Docs** | Grok-4 Fast (2M), Gemini (1M) | Massive context | No |

### 💡 Pro Model Tips

**Start Fast:** Begin with Gemini 2.5 Flash, GPT-OSS-120B, Kimi K2 or Grok-4 Fast. Then pivot if needed to a more capable model for validation, expansionAlways available, excellent quality, huge context.

**Context Window Sweet Spot:** 128K-1M handles most tasks. Bigger contexts risk "lost in middle" issues.

**Auto-Switching:** Kablewy automatically upgrades to bigger models when context overflows—seamless!

**Premium Models:** Add API keys (Settings → API Keys) to unlock Gemini Pro, Claude, Grok, and more!

**Skip These:** Opus, O-series, DeepSeek, and Llama not recommended. Better alternatives available.
        `
      },
      'troubleshooting': {
        icon: '🛟',
        title: 'Troubleshooting',
        content: `
# Troubleshooting

## AI Isn't Responding

### Check:
1. Is the model selector showing a valid model?
2. Do you have API keys configured? (Settings → API Keys)
3. Is your internet connection stable?

### Fix:
- Refresh the page
- Switch to a different model
- Check Settings → Integrations for API status

## Tool Calls Failing

### Symptoms:
- "Tool execution failed"
- No results returned

### Check:
1. Are the required integrations configured? (Settings → Integrations)
2. Do you have the right permissions?
3. Is the tool enabled? (Settings → Tools)

### Fix:
- Re-configure the integration
- Check API key validity
- Try a different tool

## Slow Performance

### Causes:
- Large documents processing
- Complex tool chains
- Heavy model (GPT-5, Claude Opus)

### Fix:
- Switch to a faster model (Claude Haiku, Gemini Flash)
- Break large tasks into smaller steps
- Wait for document processing to complete before querying

## Document Upload Issues

### Symptoms:
- Upload fails
- Processing stuck

### Check:
1. File size (max 50MB per file)
2. File format (supported types only)
3. Network stability

### Fix:
- Convert to supported format (PDF, DOCX, TXT)
- Break down into smaller pieces and call them "part 1, part 2 etc"
- Try uploading again

## Can't Find a Feature

### Use Chat:
*"How do I create a knowledge graph node?"*
*"Where do I configure API keys?"*

AI will guide you through the steps.
        `
      }
    };

    return sections[this.currentSection] || sections['quick-start'];
  }

  render() {
    if (!this.isOpen) return html``;

    const content = this._getContent();

    return html`
      <div class="overlay" @click=${this._close}></div>
      <div class="sidebar">
        <!-- Header -->
        <div class="header">
          <div class="header-left">
            <div class="header-icon">${content.icon}</div>
            <h2 class="header-title">Kablewy Help</h2>
          </div>
          <button class="close-button" @click=${this._close} aria-label="Close help">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Content wrapper with nav + content -->
        <div class="content-wrapper">
          <!-- Navigation -->
          <nav class="nav">
            <div class="nav-section">
              <div class="nav-section-title">Getting Started</div>
              <a class="nav-item ${this.currentSection === 'quick-start' ? 'active' : ''}" 
                 @click=${() => this._selectSection('quick-start')}>
                ⭐ Quick Start
              </a>
            </div>

            <div class="nav-section">
              <div class="nav-section-title">Features</div>
              <a class="nav-item ${this.currentSection === 'chat' ? 'active' : ''}" 
                 @click=${() => this._selectSection('chat')}>
                💬 Chat
              </a>
              <a class="nav-item ${this.currentSection === 'tools' ? 'active' : ''}" 
                 @click=${() => this._selectSection('tools')}>
                🛠️ Tools
              </a>
              <a class="nav-item ${this.currentSection === 'documents' ? 'active' : ''}" 
                 @click=${() => this._selectSection('documents')}>
                📄 Documents
              </a>
              <a class="nav-item ${this.currentSection === 'queues' ? 'active' : ''}" 
                 @click=${() => this._selectSection('queues')}>
                🧾 Queues
              </a>
              <a class="nav-item ${this.currentSection === 'tables' ? 'active' : ''}" 
                 @click=${() => this._selectSection('tables')}>
                📊 Tables
              </a>
              <a class="nav-item ${this.currentSection === 'graph' ? 'active' : ''}" 
                 @click=${() => this._selectSection('graph')}>
                🕸️ Graph
              </a>
              <a class="nav-item ${this.currentSection === 'memory' ? 'active' : ''}" 
                 @click=${() => this._selectSection('memory')}>
                🧠 Memory
              </a>
              <a class="nav-item ${this.currentSection === 'artifacts' ? 'active' : ''}" 
                 @click=${() => this._selectSection('artifacts')}>
                🖼️ Artifacts
              </a>
              <a class="nav-item ${this.currentSection === 'integrations' ? 'active' : ''}" 
                 @click=${() => this._selectSection('integrations')}>
                🔗 Apps
              </a>
              <a class="nav-item ${this.currentSection === 'media' ? 'active' : ''}" 
                 @click=${() => this._selectSection('media')}>
                🎨 Media
              </a>
              <a class="nav-item ${this.currentSection === 'settings' ? 'active' : ''}" 
                 @click=${() => this._selectSection('settings')}>
                ⚙️ Settings
              </a>
            </div>

            <div class="nav-section">
              <div class="nav-section-title">Advanced</div>
              <a class="nav-item ${this.currentSection === 'workflows' ? 'active' : ''}" 
                 @click=${() => this._selectSection('workflows')}>
                🎯 Workflows
              </a>
              <a class="nav-item ${this.currentSection === 'prompting' ? 'active' : ''}" 
                 @click=${() => this._selectSection('prompting')}>
                ✍️ Prompting Tips
              </a>
              <a class="nav-item ${this.currentSection === 'tips' ? 'active' : ''}" 
                 @click=${() => this._selectSection('tips')}>
                💡 Power Tips
              </a>
              <a class="nav-item ${this.currentSection === 'troubleshooting' ? 'active' : ''}" 
                 @click=${() => this._selectSection('troubleshooting')}>
                🛟 Troubleshooting
              </a>
            </div>
          </nav>

          <!-- Content -->
          <div class="content">
            <div class="section-header">
              <div class="section-icon">${content.icon}</div>
              <h1 class="section-title">${content.title}</h1>
            </div>
            <markdown-element .markdown=${content.content}></markdown-element>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('help-sidebar', HelpSidebar);
